import { RangeValue } from '@common/types/queryState';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Badge, Button, Flex, IconButton, Text } from '@radix-ui/themes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFacetingContext } from '../context';
import { useBooleanFacetItems } from '../hooks/useBooleanFacetItems';
import { FACET_MISSING_KEY } from '../constants';

interface ActiveBadgeProps {
    id?: string;
    sectionLabel: string;
    valueLabel: string;
    onRemove: () => void;
}

const formatRangeLabel = (range: RangeValue): string | null => {
    const hasMin = range.min !== undefined;
    const hasMax = range.max !== undefined;

    if (hasMin && hasMax) return `${range.min} - ${range.max}`;
    if (hasMin) return `≥ ${range.min}`;
    if (hasMax) return `≤ ${range.max}`;

    return null;
};

const FilterBadge = ({
    sectionLabel,
    valueLabel,
    onRemove,
}: ActiveBadgeProps) => (
    <Badge color='gray' variant='soft' radius='full' size='2'>
        <Text highContrast>
            <strong>{sectionLabel}:</strong>
        </Text>

        {valueLabel}

        <IconButton
            size={'1'}
            variant='ghost'
            onClick={onRemove}
            aria-label={`Remove ${sectionLabel} filter`}
        >
            <Cross2Icon />
        </IconButton>
    </Badge>
);

const BooleanSummaryGroup = ({ facetKey }: { facetKey: string }) => {
    const { getSectionLabel } = useFacetingContext();
    const { activeSummaryItems, handleToggle } = useBooleanFacetItems(facetKey);

    if (activeSummaryItems.length === 0) return null;

    return (
        <>
            {activeSummaryItems.map((item) => (
                <FilterBadge
                    key={`${facetKey}-${item.id}`}
                    sectionLabel={getSectionLabel(facetKey)}
                    valueLabel={item.label}
                    onRemove={() => handleToggle(item)}
                />
            ))}
        </>
    );
};

export const FilterSummary = () => {
    const { t } = useTranslation('facetingPanel');
    const { filterState, getSectionLabel } = useFacetingContext();

    const { selectedFacets, selectedRanges, setRangeFilter, clearAll } =
        filterState;

    const rangeBadges = useMemo(() => {
        return Object.entries(selectedRanges)
            .map(([key, range]): ActiveBadgeProps | null => {
                if (!range) return null;

                const valueLabel =
                    range.operator === FACET_MISSING_KEY
                        ? t(FACET_MISSING_KEY)
                        : formatRangeLabel(range);

                if (!valueLabel) return null;

                return {
                    id: `range-${key}`,
                    sectionLabel: getSectionLabel(key),
                    valueLabel,
                    onRemove: () =>
                        setRangeFilter(key, {
                            min: undefined,
                            max: undefined,
                        }),
                };
            })
            .filter((badge): badge is ActiveBadgeProps => badge !== null);
    }, [selectedRanges, getSectionLabel, t, setRangeFilter]);

    const activeBooleanKeys = Object.keys(selectedFacets).filter(
        (key) => selectedFacets[key] && selectedFacets[key]!.length > 0,
    );

    const hasActiveFilters =
        activeBooleanKeys.length > 0 || rangeBadges.length > 0;

    if (!hasActiveFilters) return null;

    return (
        <Flex direction='column' gap='2'>
            <Flex wrap='wrap' gap='2'>
                {activeBooleanKeys.map((key) => (
                    <BooleanSummaryGroup
                        key={`bool-group-${key}`}
                        facetKey={key}
                    />
                ))}

                {rangeBadges.map((badge) => (
                    <FilterBadge key={badge.id} {...badge} />
                ))}
            </Flex>

            <Flex justify='end'>
                <Button
                    variant='ghost'
                    size='1'
                    color='gray'
                    onClick={clearAll}
                    style={{ cursor: 'pointer' }}
                >
                    {t('clearAll')}
                </Button>
            </Flex>
        </Flex>
    );
};
